// ClearableInput.js
import React, { useRef, forwardRef, useEffect } from 'react';

const ClearableInput = forwardRef(({
  value,
  onChange,
  placeholder,
  isMultiLine = false,
  name,
  onKeyDown,
  disabled = false,
  className = '',
}, ref) => {
  const inputRef = useRef(null);

  const clearField = () => {
    onChange({ target: { name, value: '' } });
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const setRefs = (el) => {
    inputRef.current = el;
    if (typeof ref === 'function') {
      ref(el);
    } else if (ref) {
      ref.current = el;
    }
  };

  const autoResize = () => {
    if (isMultiLine && inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    autoResize();
  }, [value]);

  const handleChange = (e) => {
    onChange(e);
    autoResize();
  };

  const handlePaste = (e) => {
    if (isMultiLine) {
      e.preventDefault();
      
      const pastedText = e.clipboardData.getData('text')
        .replace(/\r\n/g, '\n')  // Convert Windows line breaks
        .replace(/\r/g, '\n')    // Convert Mac line breaks
        .replace(/\n\n+/g, '\n\n'); // Normalize multiple line breaks
      
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      
      const newValue = value.substring(0, start) + 
                      pastedText + 
                      value.substring(end);
      
      onChange({
        target: {
          name,
          value: newValue
        }
      });

      setTimeout(() => {
        if (inputRef.current) {
          const newCursorPos = start + pastedText.length;
          inputRef.current.selectionStart = newCursorPos;
          inputRef.current.selectionEnd = newCursorPos;
          autoResize();
        }
      }, 0);
      return;
    }

    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/[\r\n]+/g, ' ');
    onChange({
      target: {
        name,
        value: pastedText
      }
    });
  };

  return (
    <div className={`clearable-input-container ${className}`}>
      {isMultiLine ? (
        <textarea
          ref={setRefs}
          value={value}
          onChange={handleChange}
          onPaste={handlePaste}
          placeholder={placeholder}
          name={name}
          onKeyDown={onKeyDown}
          disabled={disabled}
          className={`clearable-textarea ${className}`}
          rows={1}
        />
      ) : (
        <input
          ref={setRefs}
          type="text"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          name={name}
          onKeyDown={onKeyDown}
          disabled={disabled}
          className={`clearable-input ${className}`}
        />
      )}
      {value && (
        <button
          className="clear-button"
          onClick={clearField}
          type="button"
          aria-label="Clear input"
        >
          ×
        </button>
      )}
    </div>
  );
});

export default ClearableInput;
